import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { CalendarIcon, PlayCircleIcon } from '@heroicons/react/24/solid';
import { AnimatePresence, motion } from 'framer-motion';
import { type ReactNode, useState } from 'react';

import { cn } from '@/lib/utils';

import { DateRangePicker } from './DateRangePicker';
import { DaySelector } from './DaySelector';
import { ScheduleTimeInput } from './ScheduleTimeInput';
import { ScheduleWarning } from './ScheduleWarning';
import { type WeeklyScheduleState } from './WeeklySchedule';

export interface ScheduleConfig {
  startDate: string;
  endDate: string;
  schedule: WeeklyScheduleState;
}

export interface ExecutionModeSelectorProps {
  mode: 'now' | 'schedule';
  onModeChange: (mode: 'now' | 'schedule') => void;
  scheduleConfig: ScheduleConfig;
  onScheduleConfigChange: (config: ScheduleConfig) => void;
  showLargeArtifact: boolean;
  largeArtifact: boolean;
  onLargeArtifactChange: (value: boolean) => void;
  /** Number of targets selected - used to show multiple schedule warning */
  targetCount?: number;
}

// Day key mapping between DaySelector format and WeeklyScheduleState
const DAY_KEY_MAP: Record<string, keyof WeeklyScheduleState> = {
  mon: 'monday',
  tue: 'tuesday',
  wed: 'wednesday',
  thu: 'thursday',
  fri: 'friday',
  sat: 'saturday',
  sun: 'sunday',
};

const REVERSE_DAY_KEY_MAP: Record<keyof WeeklyScheduleState, string> = {
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat',
  sunday: 'sun',
};

/**
 * Convert WeeklyScheduleState to array of selected day keys
 */
function getSelectedDays(schedule: WeeklyScheduleState): string[] {
  return Object.entries(schedule)
    .filter(([_, daySchedule]) => daySchedule.enabled)
    .map(([day]) => REVERSE_DAY_KEY_MAP[day as keyof WeeklyScheduleState]);
}

/**
 * Get single time from schedule (first enabled day or default)
 */
function getSingleTime(schedule: WeeklyScheduleState): string {
  const enabledDay = Object.values(schedule).find(d => d.enabled);
  return enabledDay?.time || '09:00';
}

/**
 * Get per-day times from schedule
 */
function getPerDayTimes(schedule: WeeklyScheduleState): Record<string, string> {
  const times: Record<string, string> = {};
  Object.entries(schedule).forEach(([day, daySchedule]) => {
    if (daySchedule.enabled) {
      times[REVERSE_DAY_KEY_MAP[day as keyof WeeklyScheduleState]] =
        daySchedule.time;
    }
  });
  return times;
}

/**
 * Check if schedule uses different times per day
 */
function usesDifferentTimes(schedule: WeeklyScheduleState): boolean {
  const enabledDays = Object.values(schedule).filter(d => d.enabled);
  if (enabledDays.length <= 1) return false;
  const firstTime = enabledDays[0]?.time;
  return enabledDays.some(d => d.time !== firstTime);
}

/**
 * ExecutionModeSelector - Radio group for choosing between Run Now and Schedule for Later
 *
 * Replaces the old tab-based navigation with inline radio buttons and
 * progressive disclosure of the schedule form.
 *
 * Key features:
 * - Two radio options: Run Now and Schedule for Later
 * - Large artifact output checkbox (visible in Run Now mode when capability supports it)
 * - Animated schedule form that slides in when Schedule for Later is selected
 * - Warning banner immediately visible in schedule mode
 * - Compact pill-based day selector (Phase 4)
 * - Single time input with local time display
 * - Per-day time expansion option
 * - Multiple target warning when applicable
 */
export function ExecutionModeSelector({
  mode,
  onModeChange,
  scheduleConfig,
  onScheduleConfigChange,
  showLargeArtifact,
  largeArtifact,
  onLargeArtifactChange,
  targetCount = 0,
}: ExecutionModeSelectorProps) {
  // Track whether per-day mode is being used (used by handlePerDayTimesChange)
  const [, setIsPerDayMode] = useState(() =>
    usesDifferentTimes(scheduleConfig.schedule)
  );

  // Derived state from scheduleConfig
  const selectedDays = getSelectedDays(scheduleConfig.schedule);
  const singleTime = getSingleTime(scheduleConfig.schedule);
  const perDayTimes = getPerDayTimes(scheduleConfig.schedule);

  /**
   * Handle day selection changes from DaySelector
   */
  const handleDaysChange = (newDays: string[]) => {
    const newSchedule = { ...scheduleConfig.schedule };

    // Disable all days first
    Object.keys(newSchedule).forEach(day => {
      newSchedule[day as keyof WeeklyScheduleState] = {
        ...newSchedule[day as keyof WeeklyScheduleState],
        enabled: false,
      };
    });

    // Enable selected days
    newDays.forEach(dayKey => {
      const fullDayKey = DAY_KEY_MAP[dayKey];
      if (fullDayKey) {
        newSchedule[fullDayKey] = {
          ...newSchedule[fullDayKey],
          enabled: true,
          // Keep existing time or use default
          time: newSchedule[fullDayKey].time || singleTime,
        };
      }
    });

    onScheduleConfigChange({
      ...scheduleConfig,
      schedule: newSchedule,
    });
  };

  /**
   * Handle single time change (applies to all selected days)
   */
  const handleTimeChange = (newTime: string) => {
    const newSchedule = { ...scheduleConfig.schedule };

    // Update time for all enabled days
    Object.keys(newSchedule).forEach(day => {
      if (newSchedule[day as keyof WeeklyScheduleState].enabled) {
        newSchedule[day as keyof WeeklyScheduleState] = {
          ...newSchedule[day as keyof WeeklyScheduleState],
          time: newTime,
        };
      }
    });

    onScheduleConfigChange({
      ...scheduleConfig,
      schedule: newSchedule,
    });
  };

  /**
   * Handle per-day time changes
   */
  const handlePerDayTimesChange = (newTimes: Record<string, string>) => {
    const newSchedule = { ...scheduleConfig.schedule };

    // Update times for specific days
    Object.entries(newTimes).forEach(([dayKey, time]) => {
      const fullDayKey = DAY_KEY_MAP[dayKey];
      if (fullDayKey && newSchedule[fullDayKey].enabled) {
        newSchedule[fullDayKey] = {
          ...newSchedule[fullDayKey],
          time,
        };
      }
    });

    onScheduleConfigChange({
      ...scheduleConfig,
      schedule: newSchedule,
    });

    setIsPerDayMode(true);
  };

  const handleStartDateChange = (startDate: string) => {
    onScheduleConfigChange({
      ...scheduleConfig,
      startDate,
    });
  };

  const handleEndDateChange = (endDate: string) => {
    onScheduleConfigChange({
      ...scheduleConfig,
      endDate,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div
        role="radiogroup"
        aria-label="Execution mode"
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        <ModeCard
          selected={mode === 'now'}
          onSelect={() => onModeChange('now')}
          icon={<PlayCircleIcon className="size-5" />}
          title="Run Now"
          description="Start the scan immediately."
          ariaControls={undefined}
        />
        <ModeCard
          selected={mode === 'schedule'}
          onSelect={() => onModeChange('schedule')}
          icon={<CalendarIcon className="size-5" />}
          title="Schedule"
          description="Run on a recurring weekly schedule."
          ariaControls="schedule-form-container"
        />
      </div>

      {mode === 'now' && showLargeArtifact && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={largeArtifact}
            onChange={e => onLargeArtifactChange(e.target.checked)}
            className="accent-chariot-brand-primary"
            aria-label="Enable large artifact output"
          />
          <span className="text-chariot-text-secondary">
            Enable large artifact output
          </span>
        </label>
      )}

      {/* Schedule Form - Animated slide-in when schedule mode selected */}
      <AnimatePresence mode="wait">
        {mode === 'schedule' && (
          <motion.div
            key="schedule-form"
            id="schedule-form-container"
            data-testid="schedule-form-container"
            role="region"
            aria-label="Schedule configuration"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="ml-7 overflow-hidden"
          >
            <div className="space-y-6 pt-2">
              <ScheduleWarning />

              {/* Day Selector (Phase 4) */}
              <DaySelector
                selectedDays={selectedDays}
                onDaysChange={handleDaysChange}
              />

              {/* Time Input with local time display (Phase 4) */}
              <ScheduleTimeInput
                time={singleTime}
                onTimeChange={handleTimeChange}
                selectedDays={selectedDays}
                perDayTimes={perDayTimes}
                onPerDayTimesChange={handlePerDayTimesChange}
              />

              {/* Date Range Picker */}
              <DateRangePicker
                startDate={scheduleConfig.startDate}
                endDate={scheduleConfig.endDate}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
              />

              {/* Multiple target warning (Phase 4) */}
              {targetCount > 1 && (
                <div
                  data-testid="multiple-target-warning"
                  className="bg-chariot-background-secondary flex items-center gap-2 rounded-md p-3"
                >
                  <InformationCircleIcon className="text-chariot-text-secondary size-5 flex-shrink-0" />
                  <span className="text-chariot-text-secondary text-sm">
                    This will create {targetCount} schedules (one per target)
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Card-style radio option for execution mode. Replaces the bare radio
 * button + label pattern with a decisive bordered card that fills its
 * column and reads as a tappable target.
 */
interface ModeCardProps {
  selected: boolean;
  onSelect: () => void;
  icon: ReactNode;
  title: string;
  description: string;
  ariaControls?: string;
}

function ModeCard({
  selected,
  onSelect,
  icon,
  title,
  description,
  ariaControls,
}: ModeCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-controls={ariaControls}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-3 rounded-md border p-3 text-left transition-colors',
        'focus-visible:ring-chariot-brand-primary focus:outline-hidden focus-visible:ring-2',
        selected
          ? 'border-chariot-brand-primary bg-chariot-brand-primary/10'
          : 'border-chariot-stroke hover:border-chariot-stroke/80 hover:bg-chariot-background-secondary/40'
      )}
    >
      <div
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-md',
          selected
            ? 'bg-chariot-brand-primary text-white'
            : 'bg-chariot-background-secondary text-chariot-text-secondary'
        )}
      >
        {icon}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-chariot-text-primary text-sm font-semibold">
          {title}
        </span>
        <span className="text-chariot-text-secondary truncate text-xs">
          {description}
        </span>
      </div>
      <span
        aria-hidden="true"
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
          selected
            ? 'border-chariot-brand-primary bg-chariot-brand-primary'
            : 'border-chariot-stroke'
        )}
      >
        {selected && (
          <span className="bg-chariot-background-primary size-1.5 rounded-full" />
        )}
      </span>
    </button>
  );
}
